import type {
  ColumnBuilderParams,
  CrossTableMode,
  DataBuilderParams,
} from '@/features/(charts)/chart-renderer/ui/Cross/types';

import { CompareStrategy } from './CompareStrategy';
import type { ICrossTableStrategy } from './ICrossTableStrategy';
import { MultiSeriesStrategy } from './MultiSeriesStrategy';

/**
 * CrossTable Strategy Factory
 *
 * 차트 구성에 따라 적절한 CrossTable Strategy를 생성하는 팩토리 클래스입니다.
 *
 * @remarks
 * 이 팩토리는 다음 세 가지 모드를 지원합니다:
 * - `single`: 단일 시리즈, 비교 없음
 * - `compare`: 단일 시리즈, 비교 모드
 * - `multiSeries`: 다중 시리즈
 *
 * @example
 * ```typescript
 * // mode 판별
 * const mode = CrossTableStrategyFactory.determineMode(chart);
 *
 * // Strategy 생성
 * const strategy = CrossTableStrategyFactory.createStrategy(
 *   mode,
 *   columnParams,
 *   dataParams
 * );
 *
 * // 컬럼 및 데이터 생성
 * const columns = strategy.buildColumns();
 * const dataSource = strategy.buildDataSource();
 * ```
 */
export class CrossTableStrategyFactory {
  /**
   * 차트 구성에 따라 적절한 CrossTable 모드를 판별합니다.
   *
   * @param chart - 차트 정보
   * @returns CrossTableMode
   *
   * @remarks
   * 판별 우선순위:
   * 1. series.length > 1 → `multiSeries`
   * 2. target.is_compare === true → `compare`
   * 3. 그 외 → `single`
   *
   * @example
   * ```typescript
   * const chart = { series: [s1, s2], target: { is_compare: false } };
   * const mode = CrossTableStrategyFactory.determineMode(chart);
   * // mode === 'multiSeries'
   * ```
   */
  static determineMode(chart: ChartBase): CrossTableMode {
    if (chart.series.length > 1) {
      return 'multiSeries';
    }
    if (chart.target.is_compare) {
      return 'compare';
    }
    return 'single';
  }

  /**
   * 지정된 모드에 맞는 CrossTable Strategy 인스턴스를 생성합니다.
   *
   * @param mode - CrossTable 모드 (`single` | `compare` | `multiSeries`)
   * @param columnParams - Column Builder에 전달할 파라미터
   * @param dataParams - Data Generator에 전달할 파라미터
   * @returns ICrossTableStrategy 구현체
   *
   * @remarks
   * 각 모드별로 다음 Strategy가 생성됩니다:
   * - `single`: SingleSeriesStrategy
   * - `compare`: CompareStrategy
   * - `multiSeries`: MultiSeriesStrategy
   *
   * @example
   * ```typescript
   * const strategy = CrossTableStrategyFactory.createStrategy(
   *   'single',
   *   columnParams,
   *   dataParams
   * );
   * const columns = strategy.buildColumns();
   * ```
   */
  static createStrategy(
    mode: CrossTableMode,
    columnParams: ColumnBuilderParams,
    dataParams: DataBuilderParams,
  ): ICrossTableStrategy {
    switch (mode) {
      case 'compare':
        return new CompareStrategy(columnParams, dataParams);
      case 'multiSeries':
      default:
        return new MultiSeriesStrategy(columnParams, dataParams);
    }
  }
}
